import { SignInButton, SignedIn, SignedOut, useAuth } from "@clerk/clerk-react";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { PlaidLinkLaunchButton } from "../components/PlaidLinkLaunchButton";
import { resolveClerkPublishableKey } from "../clerk-env";
import {
  createCustomerBankLinkToken,
  exchangeCustomerBankPublicToken,
  fetchCustomerBankProfile,
  formatBankLinkExchangeError,
  formatBankLinkStepError,
  type CustomerBankLinkProfile,
} from "../lib/customer-plaid-bank-client";
import { fetchMe, formatMeFetchError } from "../lib/fetch-with-clerk";

function Header() {
  return (
    <header className="space-y-2 border-b border-[var(--card-border)] pb-6">
      <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--green)]">Account</p>
      <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">Payment methods</h1>
      <p className="text-sm text-[var(--text-secondary)]">
        ACH checkout uses Plaid Link and Straddle. Link a checking account here or during checkout (same profile).
      </p>
    </header>
  );
}

export function AccountPaymentScreen() {
  const clerkPk = resolveClerkPublishableKey();
  if (!clerkPk) {
    return (
      <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6 pb-16">
        <Header />
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 shadow-cardMd">
          <p className="text-sm text-amber-900">
            Set <span className="font-mono">VITE_CLERK_PUBLISHABLE_KEY</span> to manage bank linking.
          </p>
        </div>
      </div>
    );
  }
  return <AccountPaymentInner />;
}

function AccountPaymentInner() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [meError, setMeError] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [linkedProfile, setLinkedProfile] = useState<CustomerBankLinkProfile | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [exchangeError, setExchangeError] = useState<string | null>(null);
  /** When true, fetch a fresh Link token even if a bank is already on file. */
  const [relink, setRelink] = useState(false);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    let cancelled = false;
    setMeError(null);
    void (async () => {
      try {
        const me = await fetchMe(getToken);
        if (cancelled) return;
        if (!me.user.roles.includes("customer") || !me.user.customerId) {
          setMeError(
            !me.user.roles.includes("customer")
              ? `This account does not have the customer role (primary role shown as "${me.user.role}").`
              : "No customer profile id — sign out and back in, or confirm API auto-provisioning.",
          );
          return;
        }
        setCustomerId(me.user.customerId);
      } catch (e) {
        if (!cancelled) setMeError(formatMeFetchError(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, getToken]);

  useEffect(() => {
    if (!customerId || !isSignedIn) return;
    let cancelled = false;
    setPaymentLoading(true);
    setTokenError(null);
    void (async () => {
      try {
        const profile = await fetchCustomerBankProfile(getToken, customerId);
        if (cancelled) return;
        const hasPlaid = profile.secrets.plaidItemConfigured;
        if (hasPlaid && !relink) {
          setLinkedProfile(profile);
          setLinkToken(null);
          return;
        }
        const { linkToken: tok } = await createCustomerBankLinkToken(getToken, customerId);
        if (!cancelled) {
          setLinkToken(tok);
          if (relink) setLinkedProfile(null);
        }
      } catch (e) {
        if (!cancelled) setTokenError(formatBankLinkStepError(e));
      } finally {
        if (!cancelled) setPaymentLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, isSignedIn, relink, getToken]);

  const onPlaidSuccess = useCallback(
    async (publicToken: string, metadata: { accounts?: { id: string }[] }) => {
      if (!customerId) return;
      setExchangeError(null);
      try {
        const accountId = metadata.accounts?.[0]?.id;
        const profile = await exchangeCustomerBankPublicToken(
          getToken,
          customerId,
          publicToken,
          accountId,
        );
        setLinkedProfile(profile);
        setLinkToken(null);
        setRelink(false);
      } catch (e) {
        setExchangeError(formatBankLinkExchangeError(e));
      }
    },
    [customerId, getToken],
  );

  const isLinkedAndReady = linkedProfile?.secrets.plaidItemConfigured && !relink;
  const straddleReady = linkedProfile?.secrets.straddlePaykeyConfigured ?? false;

  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6 pb-16">
      <Header />

      <SignedOut>
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 text-center shadow-cardMd">
          <p className="text-sm text-[var(--text-secondary)]">Sign in to link or view your bank on file.</p>
          <SignInButton mode="modal">
            <button
              type="button"
              className="mt-4 inline-flex items-center rounded-lg bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--green-dark)]"
            >
              Sign in
            </button>
          </SignInButton>
        </div>
      </SignedOut>

      <SignedIn>
        {meError ? (
          <div
            role="alert"
            className="rounded-2xl border border-[var(--badge-red)] bg-[var(--status-error-bg)] p-4 text-sm text-[var(--status-error-text)]"
          >
            {meError}
          </div>
        ) : isLinkedAndReady && linkedProfile ? (
          <div className="space-y-4 rounded-2xl border border-[var(--card-border)] bg-white p-5 shadow-cardMd">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-[var(--text-primary)]">
                    {linkedProfile.bankName ?? "Bank account"}
                  </p>
                  <span className="inline-flex items-center rounded-full bg-[var(--surface-sage)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--green-dark)]">
                    Linked
                  </span>
                </div>
                {linkedProfile.bankAccountLast4 ? (
                  <p className="mt-1 font-mono text-sm text-[var(--text-muted)]">
                    ···· {linkedProfile.bankAccountLast4}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => {
                  setExchangeError(null);
                  setRelink(true);
                }}
                className="text-xs font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--green)]"
              >
                Use a different bank
              </button>
            </div>
            <p className="text-xs text-[var(--text-muted)]">
              Straddle paykey:{" "}
              {straddleReady ? (
                <span className="text-[var(--green-dark)]">ready for ACH</span>
              ) : (
                <span className="text-amber-900">not configured — link again after API / Straddle setup</span>
              )}
            </p>
          </div>
        ) : paymentLoading ? (
          <p className="text-sm text-[var(--text-muted)]">Loading Plaid…</p>
        ) : (
          <div className="space-y-3 rounded-2xl border border-[var(--card-border)] bg-white p-5 shadow-cardMd">
            {relink ? (
              <p className="text-sm text-[var(--text-secondary)]">
                Link a new account with Plaid. Your previous link will be replaced when this succeeds.
              </p>
            ) : (
              <p className="text-sm text-[var(--text-secondary)]">No bank linked yet — link one to enable ACH checkout.</p>
            )}
            {tokenError ? (
              <p
                role="alert"
                className="inline-flex items-center rounded-full bg-[var(--status-error-bg)] px-3 py-1 text-xs font-medium text-[var(--status-error-text)]"
              >
                {tokenError}
              </p>
            ) : null}
            {exchangeError ? (
              <p
                role="alert"
                className="inline-flex items-center rounded-full bg-[var(--status-error-bg)] px-3 py-1 text-xs font-medium text-[var(--status-error-text)]"
              >
                {exchangeError}
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-3">
              {linkToken ? (
                <PlaidLinkLaunchButton
                  linkToken={linkToken}
                  onPlaidSuccess={onPlaidSuccess}
                  label={relink ? "Update bank with Plaid" : "Link bank with Plaid"}
                />
              ) : null}
              {relink ? (
                <button
                  type="button"
                  onClick={() => {
                    setRelink(false);
                    setTokenError(null);
                    setExchangeError(null);
                  }}
                  className="text-sm font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--green)]"
                >
                  Cancel
                </button>
              ) : null}
            </div>
          </div>
        )}
      </SignedIn>

      <div className="border-t border-[var(--card-border)] pt-6">
        <Link
          to="/"
          className="text-sm font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--green)]"
        >
          ← Back to marketplace
        </Link>
      </div>
    </div>
  );
}
